/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import * as amplitude from '@amplitude/analytics-browser';
import type { EnrichmentPlugin } from '@amplitude/analytics-types';
import { Experiment } from '@amplitude/experiment-js-client';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

import { ampStore } from '@fai/friendli-suite/shared/amplitude';
import { environment } from '@fai/friendli-suite/shared/environments/environment';

function replaceBetween(
  pathname: string,
  replacements: Array<{ start: string; end: string; replacement: string }>
) {
  let result = pathname;

  replacements.forEach(({ start, end, replacement }) => {
    const regex = new RegExp(`${start}([^/]+)${end ? end : ''}`); // Matches text between start and end, end can be empty
    result = result.replace(regex, `${start}${replacement}${end ? end : ''}`);
  });

  return result;
}

const replacements: Parameters<typeof replaceBetween>[1] = [
  { start: 'team/', end: '/', replacement: ':teamId' },
  { start: 'project/', end: '/', replacement: ':projectId' },
  { start: 'endpoint/', end: '/', replacement: ':endpointId' },
  { start: 'fine-tuning/', end: '/', replacement: ':fineTuningId' },
  { start: 'model/', end: '/', replacement: ':modelId' },
  { start: 'repository/', end: '/', replacement: ':repositoryId' },
  { start: 'dataset/', end: '/', replacement: ':datasetId' },
  { start: 'model/chat/', end: '', replacement: ':modelId' }, // No end since it's the last part
  { start: 'tag/', end: '', replacement: ':tagId' },
];

const pageViewTrackingEnrichment = (): EnrichmentPlugin => {
  return {
    name: 'page-view-tracking-enrichment',
    type: 'enrichment',
    setup: async () => undefined,
    execute: async (event) => {
      if (event.event_type !== '[Amplitude] Page Viewed') {
        return event;
      }

      event.event_properties = {
        ...event.event_properties,
        suitePagePath: replaceBetween(window.location.pathname, replacements),
      };

      return event;
    },
  };
};

const sessionReplayTracking = sessionReplayPlugin();

amplitude.add(sessionReplayTracking);
amplitude.add(pageViewTrackingEnrichment());

/**
 * @url https://amplitude.com/docs/apis/keys-and-tokens
 * @note api keys are not secret
 */
if (environment.AMPLITUDE_API_KEY) {
  amplitude.init(environment.AMPLITUDE_API_KEY, {
    instanceName: environment.ENV_NAME,
    serverZone: 'US',
    defaultTracking: {
      sessions: true,
      pageViews: true,
      formInteractions: false,
      fileDownloads: false,
    },
  });
}

export const experiment = Experiment.initialize(environment.AMPLITUDE_DEPLOYMENT_API_KEY);

const { ampIdentify, identify } = ampStore.getState();
ampIdentify.add('screen-width', window.screen.width);
ampIdentify.add('screen-height', window.screen.height);
identify().promise.then(async ({ event }) => {
  const user = {
    user_id: event.user_id,
    device_id: event.device_id,
  };

  await experiment.fetch(user);
});
